import React, { useEffect, useRef, useState } from 'react';
import { MoreVertical } from 'lucide-react';

/**
 * Per-number "More" dropdown (Set as Default / Inactive / Special / Detach / Delete).
 * @param {{ number: Object, onAction: (action: 'default'|'status'|'special'|'detach'|'delete') => void }} props
 * @returns {JSX.Element}
 */
export default function NumberActionsMenu({ number, onAction }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const inactive = number.status === 'inactive';
  const items = [
    { id: 'default', label: 'Set as Default', hide: number.isDefault || inactive },
    { id: 'status', label: inactive ? 'Set as Active' : 'Set as Inactive' },
    { id: 'special', label: number.isSpecial ? 'Remove Special Number' : 'Special Number', hide: inactive && !number.isSpecial },
    { id: 'detach', label: 'Detach from Leads' },
    { id: 'delete', label: 'Delete Phone Number', danger: true },
  ].filter((i) => !i.hide);

  return (
    <div ref={ref} className="relative" data-testid="number-actions">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`More actions for ${number.number}`}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-civix-purple/40 text-civix-purpleSoft text-xs font-semibold hover:bg-civix-purple/10"
      >
        <MoreVertical className="w-3.5 h-3.5" /> More
      </button>
      {open && (
        <ul role="menu" className="absolute right-0 bottom-full mb-1 z-30 min-w-[11rem] rounded-lg border border-white/10 bg-civix-panel shadow-xl py-1">
          {items.map((i) => (
            <li key={i.id} role="none">
              <button
                type="button"
                role="menuitem"
                onClick={() => { setOpen(false); onAction(i.id); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-white/5 ${i.danger ? 'text-rose-300' : 'text-white/85'}`}
              >
                {i.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
